import type { ComponentConfig } from '@measured/puck';
import type { ReactNode } from 'react';
import { ArrowRight, ShieldCheck, Sparkles, Timer } from 'lucide-react';
import { Container } from '../layout/Container';
import { ComparateurFunnel } from '../tools/ComparateurFunnel';
import { DiagnosticFunnel } from '../tools/DiagnosticFunnel';
import { AidsCalculatorFunnel } from '../tools/AidsCalculatorFunnel';
import { DecretTertiaireDiagnostic } from '../tools/DecretTertiaireDiagnostic';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ROUTES } from '@shared/constants/routes';

export interface CalculatorEmbedProps {
  calculator: 'cee' | 'comparateur' | 'aides' | 'dt';
  title?: string;
}

const LABELS: Record<CalculatorEmbedProps['calculator'], string> = {
  cee: 'Estimateur CEE',
  comparateur: 'Comparateur d’énergie',
  aides: 'Calculateur d’aides',
  dt: 'Diagnostic Décret Tertiaire',
};

const DEEP_LINKS: Record<CalculatorEmbedProps['calculator'], { href: string; cta: string }> = {
  cee: { href: ROUTES.diagnostic, cta: 'Lancer le diagnostic CEE' },
  comparateur: { href: ROUTES.comparateurDemarrer, cta: 'Comparer mes offres' },
  aides: { href: ROUTES.calculerMesAides, cta: 'Simuler mes aides' },
  dt: { href: ROUTES.decretTertiaire, cta: 'Lancer le diagnostic Décret Tertiaire' },
};

function FunnelShell({
  calculator,
  title,
  children,
}: {
  calculator: CalculatorEmbedProps['calculator'];
  title?: string;
  children: ReactNode;
}) {
  const displayTitle = title?.trim() || LABELS[calculator];
  return (
    <section
      className="relative bg-paper py-16 md:py-20"
      aria-labelledby={`${calculator}-embed-title`}
    >
      <Container size="narrow">
        <div className="text-center">
          <Badge variant="ivory" size="lg" className="gap-2">
            <Timer className="h-3.5 w-3.5" /> Outil interactif
          </Badge>
          <h2
            id={`${calculator}-embed-title`}
            className="mt-5 text-balance font-display text-3xl font-bold tracking-tight text-ink md:text-4xl"
          >
            {displayTitle}
          </h2>
          <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-stone-500">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-brand-700" />
              Gratuit, sans engagement
            </span>
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-accent-600" />
              Estimation chiffrée
            </span>
          </div>
        </div>
        <div className="mt-10">{children}</div>
        <div className="mt-6 text-center">
          <Button asChild variant="secondary" size="sm">
            <a href={DEEP_LINKS[calculator].href}>
              Ouvrir en page dédiée
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </Button>
        </div>
      </Container>
    </section>
  );
}

export function CalculatorEmbed({ calculator, title }: CalculatorEmbedProps) {
  if (calculator === 'comparateur') {
    return (
      <FunnelShell calculator={calculator} title={title}>
        <ComparateurFunnel />
      </FunnelShell>
    );
  }
  if (calculator === 'cee') {
    return (
      <FunnelShell calculator={calculator} title={title}>
        <DiagnosticFunnel />
      </FunnelShell>
    );
  }
  if (calculator === 'aides') {
    return (
      <FunnelShell calculator={calculator} title={title}>
        <AidsCalculatorFunnel />
      </FunnelShell>
    );
  }
  return (
    <FunnelShell calculator={calculator} title={title}>
      <DecretTertiaireDiagnostic />
    </FunnelShell>
  );
}

export const CalculatorEmbedConfig: ComponentConfig<CalculatorEmbedProps> = {
  fields: {
    title: { type: 'text' },
    calculator: {
      type: 'select',
      options: [
        { label: 'CEE', value: 'cee' },
        { label: 'Comparateur', value: 'comparateur' },
        { label: 'Aides rénovation', value: 'aides' },
        { label: 'Décret Tertiaire', value: 'dt' },
      ],
    },
  },
  defaultProps: { calculator: 'cee', title: '' },
  render: CalculatorEmbed,
};
